import {
  CarePlanActivityCode,
  LanguagePreference,
} from '@quromedical/fhir-common';
import { z } from 'zod';
import {
  EncounterDischargeStatusReason,
  EncounterPausedStatusReason,
} from '../admission';
import {
  CallOverviewBarSchema,
  CallStatusSchema,
  PatientCallSchema,
} from '../call/schema';
import {
  DisplayReferenceSchema,
  ReferenceSchema,
  UUIDSchema,
} from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import {
  PaginatedBundleSchema,
  PartialPeriodSchema,
  PeriodSchema,
  RequiredCodingSchema,
} from '../domain/schema';
import { QuestionnaireSchema } from '../questionnaire';
import { BaseServiceRequestSchema } from '../service-request';
import { PrioritySchema } from '../service-request/schema';

/******* Care Plan Activity ******/
export const CarePlanActivityType = [
  'Appointment',
  'CommunicationRequest',
  'DeviceRequest',
  'MedicationRequest',
  'Task',
  'ServiceRequest',
  'NutritionOrder',
  'VisionPrescription',
] as const;

const CarePlanActivityStatus = [
  'not-started',
  'scheduled',
  'in-progress',
  'on-hold',
  'completed',
  'cancelled',
  'stopped',
  'entered-in-error',
  'unknown',
] as const;

export const CarePlanActivityStatusSchema = z.enum(CarePlanActivityStatus);

export const CarePlanActivityTypeSchema = z.enum(CarePlanActivityType);
export const CarePlanActivityCodeSchema = z.nativeEnum(CarePlanActivityCode);

export type CarePlanActivityType = z.infer<typeof CarePlanActivityTypeSchema>;

const versionSchema = z.string().regex(/^[0-9]+(\.[0-9])?$/, {
  message:
    'Invalid version format. Must be a non-negative integer or a decimal with one digit after the decimal point.',
});

const instantiatesCanonicalSchema = z.string().refine(
  (value) => {
    const parts = value.split('|');
    const [reference, version] = parts;
    if (!ReferenceSchema.safeParse(reference).success) {
      return false;
    }
    if (version && !versionSchema.safeParse(version).success) {
      return false;
    }

    return true;
  },
  {
    message: 'Invalid canonical URI. Must be in the format: {URI}|{version}',
  },
);

export const CarePlanActivityDetailSchema = z.object({
  code: CarePlanActivityCodeSchema,
  description: z.string(),
  kind: CarePlanActivityTypeSchema,
  status: CarePlanActivityStatusSchema,
  instantiatesCanonical: z.array(instantiatesCanonicalSchema),
  scheduledPeriod: PeriodSchema,
});

export const CarePlanActivitySchema = z.object({
  detail: CarePlanActivityDetailSchema,
  /** Reference to an Activity Definition */
  reference: DisplayReferenceSchema.optional(),
});

/******* Care Plan  ******/
const SupportedCarePlanStatus = [
  'draft',
  'active',
  'on-hold',
  'completed',
  'entered-in-error',
  'unknown',
  'revoked',
] as const;

export const CarePlanIntentSchema = z.enum([
  'proposal',
  'plan',
  'order',
  'option',
]);

export const CarePlanStatusSchema = z.enum(SupportedCarePlanStatus);

export const BaseCarePlanSchema = z.object({
  activity: z.array(CarePlanActivitySchema),
  created: z.string(),
  description: z.string(),
  admission: DisplayReferenceSchema,
  status: CarePlanStatusSchema,
  title: z.string(),
  period: PartialPeriodSchema,
  author: DisplayReferenceSchema,
  careTeam: z.array(DisplayReferenceSchema),
  instantiatesCanonical: z.array(instantiatesCanonicalSchema),
  intent: CarePlanIntentSchema,
});

const ExtendCarePlanSchema = BaseCarePlanSchema.extend({
  call: PatientCallSchema.optional(),
  patient: z.object({
    reference: DisplayReferenceSchema,
    languagePreference: z.nativeEnum(LanguagePreference),
  }),
  coverage: z
    .object({
      membershipNumber: z.string(),
      coverage: UUIDSchema,
    })
    .optional(),
  priorityScore: z.number(),
});

export const CarePlanSchema = ExtendCarePlanSchema.extend({
  id: UUIDSchema,
  dailyQuestionnaires: QuestionnaireSchema.array().optional(),
});

export const CarePlanSearchSchema = PaginationSchema.extend({
  status: CarePlanStatusSchema.optional(),
  date: z.string().optional(),
  language: z.nativeEnum(LanguagePreference).optional(),
  patientId: UUIDSchema.optional(),
  patientName: z.string().optional(),
  withCalls: z.boolean().optional(),
  callStatus: CallStatusSchema.optional(),
});

export const CreateCarePlanRequestSchema = z.object({
  patientId: UUIDSchema,
  admissionId: UUIDSchema,
  planDefinition: instantiatesCanonicalSchema,
});

export const CreateCarePlanParamsSchema = z.object({
  careTeam: z.array(DisplayReferenceSchema),
  patient: DisplayReferenceSchema,
  admission: DisplayReferenceSchema,
  author: DisplayReferenceSchema,
});

export const CarePlanGetResponseSchema = CarePlanSchema;

export const CarePlanGetAllResponseSchema = PaginatedBundleSchema.extend({
  carePlans: z.array(CarePlanGetResponseSchema),
  callOverview: CallOverviewBarSchema,
});

export const CarePlanDaySchema = z.object({
  dayNumber: z.number(),
  date: z.string(),
  escalations: z.boolean(),
  flags: z.boolean(),
  appointments: z.boolean(),
  pathology: z.boolean(),
  radiology: z.boolean(),
});

export const CarePlanByPatientSchema = z.object({
  patient: DisplayReferenceSchema,
  status: CarePlanStatusSchema,
  period: PartialPeriodSchema,
  rfa: RequiredCodingSchema,
  days: CarePlanDaySchema.array(),
  pauses: PartialPeriodSchema.array().optional(),
});

export type CarePlanDay = z.infer<typeof CarePlanDaySchema>;
export type CarePlanByPatient = z.infer<typeof CarePlanByPatientSchema>;

export const CarePlanByPatientGetResponseSchema = z.array(
  CarePlanByPatientSchema,
);
export type CarePlanByPatientGetResponse = z.infer<
  typeof CarePlanByPatientGetResponseSchema
>;

export const CarePlanDischargeRequestSchema = z.object({
  id: UUIDSchema,
  periodEnd: z.string().date(),
  statusReason: EncounterDischargeStatusReason,
});

export const CarePlanPauseRequestSchema = z.object({
  id: UUIDSchema,
  statusReason: EncounterPausedStatusReason,
});

export const CreateCarePlanEscalationSchema = z.object({
  note: z.string(),
  patient: UUIDSchema,
  requestor: UUIDSchema.optional(),
  carePlan: UUIDSchema,
  admission: UUIDSchema,
  priority: PrioritySchema,
  careTeam: z.array(UUIDSchema),
});

export const CarePlanEscalationResponseSchema = z.object({
  carePlan: BaseCarePlanSchema,
  serviceRequest: BaseServiceRequestSchema,
});

export const EscalationFormSchema = z.object({
  note: z.string().min(3),
  priority: z.string().min(3),
  careTeam: z.string().min(3),
});

export type CreateCarePlanEscalation = z.infer<
  typeof CreateCarePlanEscalationSchema
>;
export type CarePlanEscalationResponse = z.infer<
  typeof CreateCarePlanEscalationSchema
>;

export type CarePlan = z.infer<typeof CarePlanSchema>;

export type CreateCarePlanRequest = z.infer<typeof CreateCarePlanRequestSchema>;
export type CreateCarePlanParams = z.infer<typeof CreateCarePlanParamsSchema>;

export type CarePlanStatus = z.infer<typeof CarePlanStatusSchema>;
export type CarePlanSearchParams = z.infer<typeof CarePlanSearchSchema>;

export type CarePlanGetResponse = z.infer<typeof CarePlanGetResponseSchema>;
export type CarePlanGetAllResponse = z.infer<
  typeof CarePlanGetAllResponseSchema
>;
